import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { useDemo } from '../../context/DemoContext';
import { Wallet, Plus, ChevronDown, CreditCard, Smartphone, Bitcoin, Check, Zap, ShieldCheck } from 'lucide-react';

const AMOUNTS = [10, 25, 50, 100];

const CRYPTO = [
  { id: 'btc', label: 'BTC', color: '#F7931A' },
  { id: 'eth', label: 'ETH', color: '#B026FF' },
  { id: 'usdt', label: 'USDT', color: '#2FBF71' },
  { id: 'usdc', label: 'USDC', color: '#E0B85A' },
];

const METHODS = [
  { id: 'visa', label: 'Visa / Mastercard', hint: 'Card', icon: CreditCard, accent: '#B026FF' },
  { id: 'licovia', label: 'LICOVIA Card', hint: 'Members card', icon: CreditCard, accent: '#E0B85A' },
  { id: 'neteller', label: 'Neteller', hint: 'E-wallet', icon: Wallet, accent: '#2FBF71' },
  { id: 'gpay', label: 'Google Pay', hint: 'Wallet', icon: Smartphone, accent: '#FF204E' },
  { id: 'apay', label: 'Apple Pay', hint: 'Wallet', icon: Smartphone, accent: '#cfcfd6' },
];

export default function FastPay({ mobile = false }) {
  const { demoBalance, addBalance } = useDemo();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(50);
  const [done, setDone] = useState(null); // { label, amount }

  const pay = (label) => {
    addBalance(amount);
    setDone({ label, amount });
    setTimeout(() => { setOpen(false); setDone(null); }, 1700);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(null); }}>
      <PopoverTrigger asChild>
        {mobile ? (
          <button className="flex w-full items-center justify-between px-3 py-3 rounded-md border border-white/10 bg-white/5 outline-none">
            <span className="flex items-center gap-2 text-white/90"><Wallet className="h-4 w-4 text-white/70" /> €{demoBalance.toFixed(2)}</span>
            <span className="flex items-center gap-1.5 text-sm font-600 text-[#2FBF71]">Add funds <span className="h-4 w-4 grid place-items-center rounded-full" style={{ background: '#2FBF71' }}><Plus className="h-3 w-3 text-[#07110b]" /></span></span>
          </button>
        ) : (
          <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-white/5 hover:border-[#2FBF71]/40 transition-colors outline-none">
            <Wallet className="h-4 w-4 text-white/70" />
            <span className="text-sm font-500 text-white/90">€{demoBalance.toFixed(2)}</span>
            <span className="h-4 w-4 grid place-items-center rounded-full" style={{ background: '#2FBF71' }}><Plus className="h-3 w-3 text-[#07110b]" /></span>
            <ChevronDown className="h-3.5 w-3.5 text-white/40" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align={mobile ? 'center' : 'end'} sideOffset={10} collisionPadding={12} className="w-80 max-w-[calc(100vw-24px)] bg-[#151119] border-white/10 text-white p-0 overflow-hidden z-[70]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between" style={{ background: 'linear-gradient(90deg,rgba(122,24,255,0.18),rgba(255,32,78,0.12))' }}>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#E0B85A]" />
            <span className="text-sm font-700 tracking-[0.14em]">FAST PAY</span>
          </div>
          <span className="text-[11px] text-white/50">€{demoBalance.toFixed(2)}</span>
        </div>

        {done ? (
          <div className="p-6 text-center xc-fade-up">
            <div className="mx-auto h-12 w-12 rounded-full grid place-items-center mb-3" style={{ background: '#2FBF7118', border: '1px solid #2FBF7155' }}>
              <Check className="h-6 w-6 text-[#2FBF71]" />
            </div>
            <div className="font-display font-700 text-lg">€{done.amount.toFixed(2)} added</div>
            <div className="text-sm text-white/55 mt-1">via {done.label}</div>
            <div className="text-[11px] text-white/35 mt-3">Demo top-up — no real payment processed.</div>
          </div>
        ) : (
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mb-2">Amount</div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {AMOUNTS.map((a) => (
                <button key={a} onClick={() => setAmount(a)}
                  className={`rounded-lg py-2 text-sm font-600 border transition-colors ${amount === a ? 'border-[#E0B85A]/60 text-[#E0B85A] bg-[#E0B85A]/10' : 'border-white/10 text-white/60 hover:text-white'}`}>
                  €{a}
                </button>
              ))}
            </div>

            <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mb-2">Pay with crypto</div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {CRYPTO.map((c) => (
                <button key={c.id} onClick={() => pay(c.label)}
                  className="rounded-lg py-2 flex flex-col items-center gap-1 border border-white/10 hover:border-white/25 hover:bg-white/[0.04] transition-colors">
                  <Bitcoin className="h-4 w-4" style={{ color: c.color }} />
                  <span className="text-[11px] font-600 text-white/80">{c.label}</span>
                </button>
              ))}
            </div>

            <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mb-2">Cards & wallets</div>
            <div className="space-y-1.5">
              {METHODS.map((m) => (
                <button key={m.id} onClick={() => pay(m.label)}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 border border-white/8 hover:border-white/20 hover:bg-white/[0.04] transition-colors text-left">
                  <span className="h-8 w-8 rounded-md grid place-items-center flex-shrink-0" style={{ background: `${m.accent}18`, border: `1px solid ${m.accent}44` }}>
                    <m.icon className="h-4 w-4" style={{ color: m.accent }} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-500 text-white/90 leading-tight">{m.label}</span>
                    <span className="block text-[11px] text-white/40">{m.hint}</span>
                  </span>
                  <span className="text-xs text-[#E0B85A] font-600">€{amount}</span>
                </button>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-white/8 flex items-start gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-white/35 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-white/35 leading-relaxed">Visual demo only — no real payments, deposits or APIs. A fast-pay shortcut for the prototype.</p>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
